import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import Markdown from "markdown-to-jsx";
import "./RenderChatBotResponse.css";
import { markdownStyleOverrides } from "../../../../lib/utils";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { getPlayerIdByName } from "../../../../Controllers/PlayerRankingApi";
import { actions } from "../../../../Redux/store";
import ClickableBold from "../ChatbotRenderComponet/ClickableBold";
import InteractiveTable from "../ChatbotRenderComponet/InteractiveTable";
import ChartRender from "../ChatbotRenderComponet/ChartRender";

interface LoadingDotsProps {
  className?: string;
}

const LoadingDots: React.FC<LoadingDotsProps> = ({ className = "" }) => {
  return (
    <span className={`inline-block ml-1 ${className}`}>
      <span className="inline-block animate-[loadingDot_1.5s_infinite] opacity-0 text-white text-xl font-bold">
        .
      </span>
      <span className="inline-block animate-[loadingDot_1.5s_infinite_0.2s] opacity-0 text-white text-xl font-bold">
        .
      </span>
      <span className="inline-block animate-[loadingDot_1.5s_infinite_0.4s] opacity-0 text-white text-xl font-bold">
        .
      </span>
    </span>
  );
};

interface RenderResponseProps {
  streamedResponse: string;
  isLoading: boolean;
  setTypingLoading: any;
  typingLoading: boolean;
  chartResponse : any
}

const RenderResponse = forwardRef<any, RenderResponseProps>(
  ({ streamedResponse, isLoading, setTypingLoading, typingLoading,chartResponse }, ref) => {
    const [displayedResponse, setDisplayedResponse] = useState("");
    const previousResponseRef = useRef("");
    const typingSpeedRef = useRef(15); // milliseconds per character
    const hapticIntervalRef = useRef<any>(null); // Reference for haptic interval
    const [hapticActive, setHapticActive] = useState(false); // Track haptic feedback state

    // Haptic feedback functions
    const hapticsImpactLight = async () => {
      await Haptics.impact({ style: ImpactStyle.Light });
    };

    // Function to start haptic feedback
    const startHapticFeedback = () => {
      if (!hapticActive) {
        setHapticActive(true);
        // Initial haptic
        hapticsImpactLight();

        // Set up interval for continuous haptic feedback while streaming
        hapticIntervalRef.current = setInterval(() => {
          hapticsImpactLight();
        }, 100); // Adjust interval as needed
      }
    };

    // Function to stop haptic feedback
    const stopHapticFeedback = () => {
      if (hapticActive) {
        setHapticActive(false);
        if (hapticIntervalRef.current) {
          clearInterval(hapticIntervalRef.current);
          hapticIntervalRef.current = null;
        }
        // Final haptic
        hapticsImpactLight();
      }
    };

    // Expose the stopHapticFeedback method to parent component
    useImperativeHandle(ref, () => ({
      stopHapticFeedback,
    }));

    // Clean up interval on unmount
    useEffect(() => {
      return () => {
        if (hapticIntervalRef.current) {
          clearInterval(hapticIntervalRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (!streamedResponse) {
        setDisplayedResponse("");
        previousResponseRef.current = "";
        return;
      }

      // If it's an error message, show it immediately
      if (streamedResponse === "RotoBot is doing a quick reboot and will be back shortly. Check back in later today.") {
        setDisplayedResponse(streamedResponse);
        return;
      }

      // If we have new content to display
      if (streamedResponse !== previousResponseRef.current) {
        // Set starting point at current display length
        let currentLength = displayedResponse.length;

        // Update reference for next comparison
        previousResponseRef.current = streamedResponse;

        // Function to add characters gradually
        const typeNextCharacter = () => {
          if (currentLength < streamedResponse.length) {
            // Add one character at a time
            setDisplayedResponse(
              streamedResponse.substring(0, currentLength + 1),
            );
            currentLength++;
            setTimeout(typeNextCharacter, typingSpeedRef.current);
          }
        };

        typeNextCharacter();
      }
    }, [streamedResponse, displayedResponse]);

    useEffect(() => {
      // When we have content to type and lengths don't match, typing is in progress
      if (streamedResponse && streamedResponse.length > 0) {
        if (streamedResponse.length !== displayedResponse.length) {
          setTypingLoading(true);
          // Start haptic feedback when typing starts
          startHapticFeedback();
        } else {
          // When lengths match, typing is complete
          setTypingLoading(false);
          // Stop haptic feedback when typing is complete
          stopHapticFeedback();
        }
      }
    }, [streamedResponse, displayedResponse, typingLoading]);

    const renderedContent = useMemo(() => {
      if (displayedResponse === "Error. Please try again.") {
        return (
          <span className="text-[14px] text-[#F11E44] font-semibold">
            Error. Please try again.
          </span>
        );
      }

      return (
        <article className="prose prose-slate prose-table:overflow-hidden max-w-none w-full ">
          <Markdown
            options={{
              overrides: {
                ...markdownStyleOverrides,
                strong: {
                  component: ClickableBold,
                },
                table: {
                  component: ({ children, ...props }) => (
                    <InteractiveTable {...props}>{children}</InteractiveTable>
                  ),
                },
                td: {
                  component: ({ children, ...props }) => (
                    <td {...props}>
                      <span className="table-data-span">{children}</span>
                    </td>
                  ),
                },
              },
            }}
          >
            {displayedResponse}
          </Markdown>
        </article>
      );
    }, [displayedResponse]);

    return (
      <>
        {chartResponse && Object.keys(chartResponse).length > 0 && (
            <ChartRender chartResponse={chartResponse} />
        )}
        {renderedContent}
        {(isLoading || typingLoading) && <LoadingDots />}
      </>
    );
  },
);

// Add display name to the forwardRef component
RenderResponse.displayName = "RenderResponse";

export default RenderResponse;
export { LoadingDots };
